// A text field with a pick list (docs-ux): the list shows the given options (filtered by what is typed) and, last, a
// "+ New ..." line that empties the field to type a new value (hidden while a value is being typed). Free text is always allowed. Keyboard: arrows, Enter, Escape.
import { useState, useRef, useId } from 'react'
import { filterOptions } from '../lib/docOptions'

export default function ComboField({ value, onChange, options, newLabel, placeholder, inputStyle, name }) {
  const [open, setOpen] = useState(false)
  const [typing, setTyping] = useState(false)   // true after a key: the list is filtered by the text; false: the whole list
  const [hi, setHi] = useState(-1)
  const inp = useRef(null)
  const id = useId()
  const shown = typing ? filterOptions(options, value) : options
  const items = [...shown.map(o => ({ ...o, kind: 'opt' })), ...(newLabel && !(typing && String(value || '').trim()) ? [{ label: newLabel, kind: 'new' }] : [])]   // while typing a value: matches only (none = no list)

  function pick(it) {
    if (it.kind === 'new') { onChange(''); setOpen(false); setTyping(true); setTimeout(() => inp.current && inp.current.focus(), 0); return }
    onChange(it.label); setOpen(false); setTyping(false); setHi(-1)
  }
  function onKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(items.length - 1, h + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)) }
    else if (e.key === 'Enter' && open && hi >= 0 && items[hi]) { e.preventDefault(); pick(items[hi]) }
    else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false) }
  }
  const sel = String(value || '').toLowerCase()
  return (
    <div style={{ position: 'relative' }}>
      <input ref={inp} type="text" name={name} value={value || ''} placeholder={placeholder} autoComplete="off"
        role="combobox" aria-expanded={open} aria-controls={id} aria-autocomplete="list"
        aria-activedescendant={open && hi >= 0 ? id + '-' + hi : undefined}
        onChange={e => { onChange(e.target.value); setTyping(true); setOpen(true); setHi(-1) }}
        onFocus={() => { setTyping(false); setOpen(true) }}
        onClick={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingRight: 30 }} />
      <button type="button" tabIndex={-1} aria-label="Show the list" onMouseDown={e => { e.preventDefault(); setTyping(false); setOpen(o => !o); inp.current && inp.current.focus() }}
        style={{ position: 'absolute', right: 2, top: 0, bottom: 0, width: 28, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 9, cursor: 'pointer' }}>▾</button>
      {open && items.length > 0 && (
        <div id={id} role="listbox" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 60, background: 'var(--card, #11161e)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 5, maxHeight: 240, overflowY: 'auto', boxShadow: '0 12px 32px rgba(0,0,0,.5)' }}>
          {items.map((it, i) => (
            <div key={it.kind + it.label} id={id + '-' + i} role="option" aria-selected={it.kind === 'opt' && it.label.toLowerCase() === sel}
              onMouseDown={e => { e.preventDefault(); pick(it) }} onMouseEnter={() => setHi(i)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '9px 10px', borderRadius: 7, cursor: 'pointer',
                borderTop: it.kind === 'new' && i > 0 ? '0.5px solid var(--border)' : 'none',
                background: i === hi ? 'rgba(201,147,58,.14)' : 'none',
                color: it.kind === 'new' ? 'var(--gold2)' : (it.label.toLowerCase() === sel ? 'var(--gold2)' : 'var(--text, #cdd6e0)'), fontWeight: it.kind === 'new' ? 600 : 400 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
              {it.kind === 'opt' && it.n > 0 && <span style={{ fontSize: 11, color: 'var(--muted)' }}>{it.n}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
